import ExcelJS from 'exceljs'
import { TFunction } from 'react-i18next'
import { parseDate } from '../../../config/dayjsConfig'
import { autoDownload } from '../../../utils/helpers'
import sanitizeFilename from '../../../utils/sanitizeFilename'
import { Match } from '../types'
import { acceptanceStatusLabel, sideStatusLabel } from '../components/reconciliationCellRenderers'
import {
  getAcceptanceStatus,
  getAmountA,
  getAmountB,
  getClassifications,
  getCompanyName,
  getCounterpartyName,
  getCurrencyLabel,
  getDateA,
  getDateB,
  getDifference,
  getPurchaseInvoiceNumber,
  getReference,
  getSalesInvoiceNumber,
  getSidePaymentStatus
} from './reconciliationHelpers'

// Mirrors the headers in reconciliationColumnDefs.ts — same translation keys, same fallback text.
const buildColumns = (t: TFunction) => [
  { header: `${t('icReconciliation:tableCompany', 'Yritys')} (A)`, key: 'companyName', width: 20 },
  { header: `${t('icReconciliation:tableCounterparty', 'Vastapuoli')} (B)`, key: 'counterpartyName', width: 20 },
  { header: t('icReconciliation:tableSalesInvoice', 'Myyntilasku A'), key: 'salesInvoiceA', width: 16 },
  { header: t('icReconciliation:tablePurchaseInvoice', 'Ostolasku B'), key: 'purchaseInvoiceB', width: 16 },
  { header: t('icReconciliation:tableReference', 'Viite'), key: 'reference', width: 16 },
  { header: t('icReconciliation:tableBookingDateA', 'Kirjauspäivä A'), key: 'dateA', width: 14 },
  { header: t('icReconciliation:tableBookingDateB', 'Kirjauspäivä B'), key: 'dateB', width: 14 },
  { header: t('icReconciliation:tableClassification', 'Luokitus'), key: 'classification', width: 16 },
  { header: t('icReconciliation:tableAcceptance', 'Käsittely'), key: 'status', width: 14 },
  { header: t('icReconciliation:tableCurrency', 'Valuutta'), key: 'currency', width: 12 },
  { header: t('icReconciliation:tableAmountA', 'Summa A'), key: 'amountA', width: 14 },
  { header: t('icReconciliation:tableAmountB', 'Summa B'), key: 'amountB', width: 14 },
  { header: t('icReconciliation:tableDifference', 'Erotus'), key: 'difference', width: 14 },
  { header: t('icReconciliation:tableStatusA', 'Tila A'), key: 'statusA', width: 14 },
  { header: t('icReconciliation:tableStatusB', 'Tila B'), key: 'statusB', width: 14 },
  { header: t('icReconciliation:acceptedBy', 'Hyväksynyt'), key: 'acceptedBy', width: 24 },
  { header: t('icReconciliation:acceptedAt', 'Hyväksytty'), key: 'acceptedAt', width: 18 },
  { header: t('icReconciliation:acceptedComment', 'Perustelu'), key: 'comment', width: 30 }
]

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)

export const exportReconciliationToExcel = async (
  matches: Match[],
  companyNameById: Map<string, string>,
  userNameById: Map<number, string>,
  groupCurrency: string,
  fileName: string,
  t: TFunction
) => {
  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'Finadeck'
  workbook.created = new Date()

  const sheet = workbook.addWorksheet(capitalize(t('icReconciliation:exportFileName', 'täsmäytys')))
  sheet.columns = buildColumns(t)

  sheet.getRow(1).font = { bold: true }

  matches.forEach(match => {
    sheet.addRow({
      companyName: getCompanyName(match, companyNameById),
      counterpartyName: getCounterpartyName(match, companyNameById),
      salesInvoiceA: getSalesInvoiceNumber(match) ?? '',
      purchaseInvoiceB: getPurchaseInvoiceNumber(match) ?? '',
      reference: getReference(match),
      dateA: getDateA(match) ?? '',
      dateB: getDateB(match) ?? '',
      classification: getClassifications(match)
        .map(classification => t(`icReconciliation:classification-${classification}`, classification))
        .join(', '),
      status: acceptanceStatusLabel(getAcceptanceStatus(match), t),
      currency: getCurrencyLabel(match, groupCurrency),
      amountA: getAmountA(match),
      amountB: getAmountB(match),
      difference: getDifference(match),
      statusA: sideStatusLabel(getSidePaymentStatus(match.a), t),
      statusB: sideStatusLabel(getSidePaymentStatus(match.b), t),
      acceptedBy:
        (match.accepted?.acceptedBy != null && userNameById.get(match.accepted.acceptedBy)) ||
        match.accepted?.acceptedByEmail ||
        '',
      acceptedAt: match.accepted?.acceptedAt
        ? parseDate(match.accepted.acceptedAt)?.format('DD.MM.YYYY HH:mm') ?? match.accepted.acceptedAt
        : '',
      comment: match.accepted?.comment ?? ''
    })
  })

  const buffer = await workbook.xlsx.writeBuffer()
  autoDownload(sanitizeFilename(fileName, 'täsmäytys', 'xlsx'), buffer)
}
